import React from 'react';
import {
    Box, Container, Typography, Grid, Paper, Stack, Button, Card, CardContent,
    Avatar, Chip, Badge
} from '@mui/material';
import {
    Psychology, Speed, Nature, EmojiEvents, Timeline, Group,
    Smartphone, CloudUpload, Security, Analytics, School, Language
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { alpha } from '@mui/material/styles';

const FeaturesPage = () => {
    const coreFeatures = [
        {
            icon: <Psychology />,
            title: "AI-Powered Classification",
            description: "Advanced machine learning model trained on thousands of waste images",
            details: "95%+ accuracy rate with real-time processing",
            color: "#43a047",
            badge: "Core"
        },
        {
            icon: <Speed />,
            title: "Lightning Fast Results",
            description: "Get instant waste classification in under 2 seconds",
            details: "Optimized for mobile and web platforms",
            color: "#00b894",
            badge: "Performance"
        },
        {
            icon: <EmojiEvents />,
            title: "Gamified Experience",
            description: "Earn champion points, badges, and climb leaderboards",
            details: "5-tier champion system with rewards",
            color: "#ffc400",
            badge: "Engagement"
        },
        {
            icon: <Timeline />,
            title: "Classification History",
            description: "Track all your waste classifications over time",
            details: "Detailed analytics and progress tracking",
            color: "#03a9f4",
            badge: "Analytics"
        }
    ];

    const advancedFeatures = [
        {
            icon: <Smartphone />,
            title: "Mobile-First Design",
            description: "Optimized for smartphones with responsive design"
        },
        {
            icon: <CloudUpload />,
            title: "Multiple Upload Methods",
            description: "Camera capture, file upload, or drag & drop"
        },
        {
            icon: <Security />,
            title: "Privacy Protected",
            description: "Your images are processed securely and not stored"
        },
        {
            icon: <Group />,
            title: "Community Leaderboards",
            description: "Compete with friends and eco-warriors globally"
        },
        {
            icon: <School />,
            title: "Educational Content",
            description: "Learn proper waste disposal methods"
        },
        {
            icon: <Language />,
            title: "Multi-language Support",
            description: "Available in Bengali and English"
        }
    ];

    return (
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8faf8 0%, #e6f4ea 100%)' }}>
            {/* Hero Section */}
            <Box sx={{
                background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                color: 'white',
                py: { xs: 4, sm: 6, md: 8 },
                px: { xs: 2, sm: 0 }
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={{ xs: 3, sm: 4 }} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <Typography
                                variant="h2"
                                fontWeight={800}
                                gutterBottom
                                sx={{
                                    fontSize: {
                                        xs: '2rem',
                                        sm: '2.5rem',
                                        md: '3.5rem'
                                    },
                                    lineHeight: { xs: 1.2, sm: 1.3 }
                                }}
                            >
                                ✨ Powerful Features
                            </Typography>
                            <Typography
                                variant="h5"
                                sx={{
                                    opacity: 0.9,
                                    mb: { xs: 3, sm: 4 },
                                    fontSize: {
                                        xs: '1.1rem',
                                        sm: '1.3rem',
                                        md: '1.5rem'
                                    },
                                    lineHeight: { xs: 1.4, sm: 1.5 }
                                }}
                            >
                                Everything you need for smart waste management in one platform
                            </Typography>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={{ xs: 1, sm: 2 }}
                                flexWrap="wrap"
                                alignItems={{ xs: 'center', sm: 'flex-start' }}
                            >
                                <Chip
                                    label="🤖 AI-Powered"
                                    sx={{
                                        background: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        height: { xs: 28, sm: 32 }
                                    }}
                                />
                                <Chip
                                    label="⚡ Real-time"
                                    sx={{
                                        background: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        height: { xs: 28, sm: 32 }
                                    }}
                                />
                                <Chip
                                    label="🏆 Gamified"
                                    sx={{
                                        background: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        height: { xs: 28, sm: 32 }
                                    }}
                                />
                                <Chip
                                    label="📱 Mobile-First"
                                    sx={{
                                        background: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        height: { xs: 28, sm: 32 }
                                    }}
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{
                                textAlign: 'center',
                                fontSize: { xs: '4rem', sm: '6rem', md: '8rem' },
                                mt: { xs: 2, md: 0 }
                            }}>
                                🚀
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Core Features */}
            <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6, md: 8 } }}>
                <Typography
                    variant="h3"
                    textAlign="center"
                    fontWeight={700}
                    gutterBottom
                    color="#1b5e20"
                    sx={{
                        fontSize: {
                            xs: '1.8rem',
                            sm: '2.4rem',
                            md: '3rem'
                        },
                        mb: { xs: 2, sm: 3 }
                    }}
                >
                    🌟 Core Features
                </Typography>
                <Typography
                    variant="h6"
                    textAlign="center"
                    color="#546e7a"
                    paragraph
                    sx={{
                        mb: { xs: 4, sm: 5, md: 6 },
                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                        maxWidth: { xs: '90%', sm: '70%', md: '60%' },
                        mx: 'auto'
                    }}
                >
                    The foundation of intelligent waste management
                </Typography>

                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                    {coreFeatures.map((feature, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <Card sx={{
                                height: '100%',
                                borderRadius: { xs: 3, sm: 4 },
                                border: `2px solid ${alpha(feature.color, 0.2)}`,
                                background: `linear-gradient(135deg, ${alpha(feature.color, 0.08)}, #ffffff)`,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: { xs: 'translateY(-4px)', sm: 'translateY(-6px)', md: 'translateY(-8px)' },
                                    boxShadow: `0 16px 48px ${alpha(feature.color, 0.15)}`
                                }
                            }}>
                                <CardContent sx={{
                                    p: { xs: 2, sm: 2.5, md: 3 },
                                    textAlign: 'center'
                                }}>
                                    <Badge
                                        badgeContent={feature.badge}
                                        color="primary"
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                                fontWeight: 600,
                                                background: feature.color
                                            }
                                        }}
                                    >
                                        <Avatar sx={{
                                            width: { xs: 60, sm: 65, md: 70 },
                                            height: { xs: 60, sm: 65, md: 70 },
                                            background: `linear-gradient(135deg, ${feature.color}, ${alpha(feature.color, 0.8)})`,
                                            mb: { xs: 1.5, sm: 2 },
                                            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
                                        }}>
                                            {feature.icon}
                                        </Avatar>
                                    </Badge>
                                    <Typography
                                        variant="h6"
                                        fontWeight={700}
                                        gutterBottom
                                        color="#1b5e20"
                                        sx={{
                                            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                                            lineHeight: { xs: 1.2, sm: 1.3 }
                                        }}
                                    >
                                        {feature.title}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="#546e7a"
                                        paragraph
                                        sx={{
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                            lineHeight: { xs: 1.3, sm: 1.4 },
                                            mb: { xs: 1.5, sm: 2 }
                                        }}
                                    >
                                        {feature.description}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: feature.color,
                                            fontWeight: 600,
                                            background: `${feature.color}20`,
                                            p: { xs: 0.75, sm: 1 },
                                            borderRadius: 2,
                                            display: 'block',
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                        }}
                                    >
                                        {feature.details}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Advanced Features Grid */}
            <Box sx={{
                background: 'linear-gradient(135deg, #ffffff, #f5fbf7)',
                py: { xs: 4, sm: 6, md: 8 }
            }}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h3"
                        textAlign="center"
                        fontWeight={700}
                        gutterBottom
                        color="#1b5e20"
                        sx={{
                            fontSize: {
                                xs: '1.8rem',
                                sm: '2.4rem',
                                md: '3rem'
                            },
                            mb: { xs: 2, sm: 3 }
                        }}
                    >
                        🔧 Advanced Capabilities
                    </Typography>
                    <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: { xs: 3, sm: 4 } }}>
                        {advancedFeatures.map((feature, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Paper sx={{
                                    p: { xs: 2.5, sm: 3 },
                                    borderRadius: { xs: 3, sm: 4 },
                                    height: '100%',
                                    background: 'linear-gradient(135deg, #ffffff, #f8faf8)',
                                    border: '2px solid #43a04715',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: { xs: 'translateY(-2px)', sm: 'translateY(-4px)' },
                                        boxShadow: '0 12px 32px rgba(67, 160, 71, 0.1)',
                                        borderColor: '#43a047'
                                    }
                                }}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: { xs: 1.5, sm: 2 },
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        textAlign: { xs: 'center', sm: 'left' }
                                    }}>
                                        <Avatar sx={{
                                            width: { xs: 45, sm: 50 },
                                            height: { xs: 45, sm: 50 },
                                            background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                                            mx: { xs: 'auto', sm: 0 }
                                        }}>
                                            {feature.icon}
                                        </Avatar>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                variant="h6"
                                                fontWeight={600}
                                                gutterBottom
                                                color="#1b5e20"
                                                sx={{
                                                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                                                    lineHeight: { xs: 1.2, sm: 1.3 }
                                                }}
                                            >
                                                {feature.title}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="#546e7a"
                                                sx={{
                                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                    lineHeight: { xs: 1.3, sm: 1.4 }
                                                }}
                                            >
                                                {feature.description}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Technical Specs */}
            <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6, md: 8 } }}>
                <Typography
                    variant="h3"
                    textAlign="center"
                    fontWeight={700}
                    gutterBottom
                    color="#1b5e20"
                    sx={{
                        fontSize: {
                            xs: '1.8rem',
                            sm: '2.4rem',
                            md: '3rem'
                        },
                        mb: { xs: 3, sm: 4 }
                    }}
                >
                    ⚙️ Technical Specifications
                </Typography>
                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ mt: { xs: 2, sm: 3, md: 4 } }}>
                    <Grid item xs={12} sm={6} md={4}>
                        <Paper sx={{
                            p: { xs: 3, sm: 4 },
                            textAlign: 'center',
                            borderRadius: { xs: 3, sm: 4 },
                            background: 'linear-gradient(135deg, #43a04708, #ffffff)',
                            border: '2px solid #43a04715',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: { xs: 'translateY(-2px)', sm: 'translateY(-4px)' },
                                boxShadow: '0 8px 24px rgba(67, 160, 71, 0.1)'
                            }
                        }}>
                            <Typography
                                variant="h2"
                                fontWeight={800}
                                color="#43a047"
                                sx={{
                                    fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' }
                                }}
                            >
                                4
                            </Typography>
                            <Typography
                                variant="h6"
                                fontWeight={600}
                                color="#1b5e20"
                                sx={{
                                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                                    mb: { xs: 0.5, sm: 1 }
                                }}
                            >
                                Waste Categories
                            </Typography>
                            <Typography
                                variant="body2"
                                color="#546e7a"
                                sx={{
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                }}
                            >
                                Recyclable, Organic, Hazardous, General
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Paper sx={{
                            p: { xs: 3, sm: 4 },
                            textAlign: 'center',
                            borderRadius: { xs: 3, sm: 4 },
                            background: 'linear-gradient(135deg, #00b89408, #ffffff)',
                            border: '2px solid #00b89415',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: { xs: 'translateY(-2px)', sm: 'translateY(-4px)' },
                                boxShadow: '0 8px 24px rgba(0, 184, 148, 0.1)'
                            }
                        }}>
                            <Typography
                                variant="h2"
                                fontWeight={800}
                                color="#00b894"
                                sx={{
                                    fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' }
                                }}
                            >
                                95%
                            </Typography>
                            <Typography
                                variant="h6"
                                fontWeight={600}
                                color="#1b5e20"
                                sx={{
                                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                                    mb: { xs: 0.5, sm: 1 }
                                }}
                            >
                                Accuracy Rate
                            </Typography>
                            <Typography
                                variant="body2"
                                color="#546e7a"
                                sx={{
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                }}
                            >
                                Validated on local waste types
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Paper sx={{
                            p: { xs: 3, sm: 4 },
                            textAlign: 'center',
                            borderRadius: { xs: 3, sm: 4 },
                            background: 'linear-gradient(135deg, #ffc40008, #ffffff)',
                            border: '2px solid #ffc40015',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: { xs: 'translateY(-2px)', sm: 'translateY(-4px)' },
                                boxShadow: '0 8px 24px rgba(255, 196, 0, 0.1)'
                            }
                        }}>
                            <Typography
                                variant="h2"
                                fontWeight={800}
                                color="#ffc400"
                                sx={{
                                    fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' }
                                }}
                            >
                                &lt;2s
                            </Typography>
                            <Typography
                                variant="h6"
                                fontWeight={600}
                                color="#1b5e20"
                                sx={{
                                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                                    mb: { xs: 0.5, sm: 1 }
                                }}
                            >
                                Processing Time
                            </Typography>
                            <Typography
                                variant="body2"
                                color="#546e7a"
                                sx={{
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                }}
                            >
                                Real-time classification
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>

            {/* CTA Section */}
            <Box sx={{
                background: 'linear-gradient(135deg, #1b5e20, #43a047)',
                color: 'white',
                py: { xs: 4, sm: 5, md: 6 },
                px: { xs: 2, sm: 0 },
                textAlign: 'center'
            }}>
                <Container maxWidth="md">
                    <Typography
                        variant="h3"
                        fontWeight={800}
                        gutterBottom
                        sx={{
                            fontSize: {
                                xs: '1.8rem',
                                sm: '2.4rem',
                                md: '3rem'
                            },
                            lineHeight: { xs: 1.2, sm: 1.3 }
                        }}
                    >
                        Experience These Features Today! 🌟
                    </Typography>
                    <Typography
                        variant="h6"
                        paragraph
                        sx={{
                            opacity: 0.9,
                            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                            maxWidth: { xs: '90%', sm: '80%' },
                            mx: 'auto'
                        }}
                    >
                        Join the revolution in smart waste management
                    </Typography>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={{ xs: 2, sm: 2 }}
                        justifyContent="center"
                        sx={{ mt: { xs: 3, sm: 4 } }}
                        alignItems="center"
                    >
                        <Button
                            component={Link}
                            to="/classify"
                            variant="contained"
                            size="large"
                            sx={{
                                background: 'linear-gradient(135deg, #ffc400, #ffea00)',
                                color: '#1b5e20',
                                fontWeight: 700,
                                px: { xs: 3, sm: 4 },
                                py: { xs: 1.2, sm: 1.5 },
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                width: { xs: '100%', sm: 'auto' },
                                maxWidth: { xs: '280px', sm: 'none' }
                            }}
                        >
                            Try All Features 🚀
                        </Button>
                        <Button
                            component={Link}
                            to="/register"
                            variant="outlined"
                            size="large"
                            sx={{
                                borderColor: 'white',
                                color: 'white',
                                fontWeight: 700,
                                px: { xs: 3, sm: 4 },
                                py: { xs: 1.2, sm: 1.5 },
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                width: { xs: '100%', sm: 'auto' },
                                maxWidth: { xs: '280px', sm: 'none' },
                                '&:hover': {
                                    borderColor: 'white',
                                    background: 'rgba(255,255,255,0.1)'
                                }
                            }}
                        >
                            Join Community 👥
                        </Button>
                    </Stack>
                </Container>
            </Box>
        </Box>
    );
};

export default FeaturesPage;
